import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isRoundBasedFormat, puntiPerIlRiposo } from '@draughts/shared';
import { useTournamentStore } from '../../store/tournament-store';
import './Tournament.css';

/**
 * «In attesa del prossimo turno», e i puntini che dicono che si sta accoppiando.
 *
 * Sta in un componente suo e non nelle pagine perche' lo rendono in DUE — la
 * lobby e la scheda del torneo — e la regola di questa attesa e' che «fra un
 * turno e l'altro tutti gli schermi dicano la stessa cosa». Due copie della
 * stessa logica sono il modo piu' rapido per tradire quella regola: basta
 * ripararne una.
 *
 * ⚠️ Perche' anche sulla scheda: a fine partita si torna li' e non piu' in
 * lobby (cambiato il 20 Set, per l'occhio dello spettatore). Chi finiva la sua
 * partita mentre gli altri tabelloni andavano avanti non leggeva piu' da
 * nessuna parte che stava aspettando: la pagina sembrava semplicemente ferma.
 *
 * Tutto quello che decide viene dallo STORE, non dalle prop: lo stato
 * dell'iscrizione, il turno corrente e il totale arrivano dal server e sono
 * identici su ogni client, quindi due browser fermi nello stesso momento non
 * possono divergere.
 */
export default function AttesaDelTurno() {
  const { t } = useTranslation();
  const status = useTournamentStore((s) => s.status);
  const currentTournament = useTournamentStore((s) => s.currentTournament);
  const tournamentFormat = useTournamentStore((s) => s.tournamentFormat);
  const currentRound = useTournamentStore((s) => s.currentRound);
  const totalRounds = useTournamentStore((s) => s.totalRounds);
  const ritornoInArrivo = useTournamentStore((s) => s.ritornoInArrivo);
  const turnoDiRiposo = useTournamentStore((s) => s.turnoDiRiposo);
  const puntiDelRiposo = useTournamentStore((s) => s.puntiDelRiposo);
  const avanzamentoTurno = useTournamentStore((s) => s.avanzamentoTurno);
  const secondiAlRitorno = useSecondiA(ritornoInArrivo);

  const torneoFinito = currentTournament?.status === 'finished' || status === 'finished';
  /* A turni, non «svizzero»: il girone ha gli stessi turni e la stessa attesa
     fra l'uno e l'altro. Chiedere `=== 'swiss'` lo mandava nel ramo dell'arena,
     che un turno da aspettare non ce l'ha affatto. */
  const aTurni = isRoundBasedFormat(tournamentFormat);

  /*
   * L'attesa non si annuncia a torneo FINITO: sopra c'e' la fascia «Torneo
   * terminato» col vincitore, e le due cose insieme promettono un turno che
   * non arrivera' mai.
   */
  if (torneoFinito) return null;
  if (status !== 'waiting' && status !== 'round-waiting') return null;

  /*
   * Il RITORNO in arrivo, che non e' un'attesa del prossimo turno.
   *
   * Uno svizzero a due manche (migration 105) rigioca la stessa coppia dentro
   * lo STESSO turno, a colori invertiti. Chi ha appena finito l'andata sta
   * aspettando una partita che comincia fra pochi secondi, non un turno nuovo:
   * dirgli «in attesa del prossimo turno» sarebbe falso due volte — sul cosa e
   * sul quando — e lo manderebbe a chiudere la scheda.
   *
   * Prima di ogni altro ramo: il turno di riposo e l'ultimo turno non possono
   * valere per chi ha un ritorno da giocare.
   */
  /* ⚠️ `!= null` e non `!== null`: lo stato puo' arrivare `undefined` — da un
     finto dello store che espone solo i campi che gli servono, o da una
     fotografia scritta prima che questo campo esistesse — e `undefined !==
     null` e' VERO, cioe' il ramo scatterebbe sempre. Nove prove dell'attesa
     fra i turni se ne sono accorte. */
  if (ritornoInArrivo != null) {
    return (
      <div className="tournament-pairing-status">
        <div className="tournament-searching">
          <div className="tournament-search-dot"></div>
          <div className="tournament-search-dot"></div>
          <div className="tournament-search-dot"></div>
        </div>
        <p className="tournament-pairing-text" data-state="return-leg">
          {secondiAlRitorno > 0
            ? t('tournament.swiss.returnLegTra', { secondi: secondiAlRitorno })
            : t('tournament.swiss.returnLegPending')}
        </p>
      </div>
    );
  }

  /*
   * L'ULTIMO turno non ha un turno dopo, e prometterlo e' una bugia: a chi ha
   * gia' finito la sua partita nel turno finale restano il testo dei risultati
   * e nessun puntino. `totalRounds > 0` e' la guardia che serve — vale 0
   * finche' il dettaglio non e' arrivato, e senza di lei `0 >= 0` spegnerebbe
   * l'attesa a chiunque.
   */
  const dopoUltimoTurno = aTurni && totalRounds > 0 && currentRound >= totalRounds;

  /*
   * Il RIPOSO, qui e non solo in lobby.
   *
   * Il messaggio stava soltanto nella lobby, ma a fine partita si torna sulla
   * scheda del torneo: chi riposava leggeva «in attesa del prossimo turno» e
   * non sapeva ne' di riposare, ne' quanti punti aveva preso, ne' che il turno
   * dopo sarebbe partito da solo. Stando in questo componente vale per
   * entrambe le pagine, com'e' la regola dell'attesa.
   */
  if (aTurni && turnoDiRiposo !== null && turnoDiRiposo === currentRound) {
    /*
     * Quanti punti, e nel GIRONE sono zero (`puntiPerIlRiposo`, shared).
     *
     * Il numero arriva dal server insieme al riposo; il ripiego serve a chi
     * rientra senza il payload, e a manche unica perche' le manche il client
     * non le sa — sbagliare per difetto qui vuol dire al massimo scrivere «+2»
     * dove erano quattro, mentre fermarsi su `SWISS_SCORING.BYE` avrebbe
     * promesso due punti a chi, in un girone, non ne prende nessuno.
     *
     * Zero non e' «+0 punti»: quella frase si legge come una beffa. E' una
     * riga sua, e sono due `t()` INTERI e non una chiave scelta da un ternario,
     * perche' la rete che controlla le chiavi invocate riconosce solo la forma
     * letterale `t('…')`.
     */
    const punti = puntiDelRiposo ?? puntiPerIlRiposo(tournamentFormat, 1);
    return (
      <div className="tournament-bye-notice" role="status" data-testid="avviso-riposo">
        <p className="tournament-bye-notice__title">
          {punti === 0
            ? t('tournament.swiss.byeRoundNessunPunto')
            : t('tournament.swiss.byeRoundPunti', { punti })}
        </p>
        <p className="tournament-bye-notice__next">
          {dopoUltimoTurno ? t('tournament.swiss.byeUltimoTurno') : t('tournament.swiss.byeProssimoTurno')}
        </p>
      </div>
    );
  }

  /*
   * Quante partite del turno mancano, e che il turno dopo non va chiamato da
   * nessuno. Prima c'era solo «in attesa»: non si sapeva se mancasse una
   * partita o tutte, ne' se servisse fare qualcosa. L'avanzamento si mostra
   * solo quando il server l'ha detto PER QUESTO turno.
   */
  const avanzamento = aTurni && avanzamentoTurno && avanzamentoTurno.round === currentRound
    ? avanzamentoTurno : null;

  return (
    <div className="tournament-pairing-status">
      {/* I puntini dicono «si sta accoppiando». Dopo l'ultimo turno non si
          accoppia piu' nessuno: resta il solo testo. */}
      {!dopoUltimoTurno && (
        <div className="tournament-searching">
          <div className="tournament-search-dot"></div>
          <div className="tournament-search-dot"></div>
          <div className="tournament-search-dot"></div>
        </div>
      )}
      <p
        className="tournament-pairing-text"
        data-state={!aTurni ? 'waiting' : dopoUltimoTurno ? 'final-results' : 'round-waiting'}
      >
        {!aTurni
          ? t('tournament.lobby.waitingForOpponent')
          : dopoUltimoTurno
            ? t('tournament.swiss.waitingFinalResults')
            : t('tournament.swiss.waitingBetweenRounds')}
      </p>
      {aTurni && (avanzamento || !dopoUltimoTurno) && (
        <p className="tournament-pairing-subtext" data-testid="attesa-dettaglio">
          {avanzamento && t('tournament.swiss.partiteConcluse', { completate: avanzamento.completate, totali: avanzamento.totali })}
          {avanzamento && !dopoUltimoTurno && ' · '}
          {!dopoUltimoTurno && t('tournament.swiss.turnoParteDaSolo')}
        </p>
      )}
    </div>
  );
}

/**
 * I secondi che mancano a `istante` (ms, orologio di questo dispositivo),
 * aggiornati una volta al secondo finche' ce ne sono. 0 quando manca niente o
 * non c'e' niente da aspettare.
 */
function useSecondiA(istante: number | null): number {
  const calcola = () => (istante == null ? 0 : Math.max(0, Math.ceil((istante - Date.now()) / 1000)));
  const [secondi, setSecondi] = useState(calcola);
  useEffect(() => {
    setSecondi(calcola());
    if (istante == null) return;
    const id = setInterval(() => {
      const s = calcola();
      setSecondi(s);
      if (s <= 0) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [istante]);
  return secondi;
}
